import { defineQueuedModal } from '@heidi/runtime/shell';
import { ModalId, SignupSource } from '@heidi/schema/ml-scribe';

/**
 * The lazy pointer every descriptor carries — and for a queued modal today it
 * has no consumer, which is the honest state of the class rather than an
 * omission here.
 *
 * The launch queue reads a modal contribution for its ELIGIBILITY METADATA
 * only: `src/atoms/launchModalsConfigAtom.ts` copies `id` / `priority` /
 * `dismissible` / `showOnce` / `showOnlyAfterAccountCreation` into the same
 * `addModalToQueue` every legacy entry uses, and never touches `load`. The
 * component keeps rendering from the mount `GlobalModalsWrapper` already owns,
 * which is what makes this flip a declaration-home change and nothing else.
 *
 * When the popup itself lands in this package this becomes
 * `() => import('../ui/dictation/new-in-this-release-popup')` and nothing else
 * about the descriptor moves. Do NOT relocate a component early to fill it in:
 * react-intl message ids are derived from the file path, so moving copy re-keys
 * the Crowdin catalogue — a user-visible change this class of PR must not make.
 */
const rendersFromItsAppMount = () => Promise.resolve({ default: null });

/**
 * The dictation "New in this release" awareness card, declared by the domain
 * that owns it (design D3) rather than by the app's central config atom.
 *
 * FROZEN IDENTITY. `id` keys backend `seen_modals` and `ModalView` dismiss
 * records, so it is the same enum member the legacy declaration carried: a
 * rename is a persisted-state migration, not a refactor, and it would resurface
 * the card for every user who has already dismissed it.
 *
 * Every input the gate reads is a field the eligibility context already
 * carries. The desktop dictation rollout arrives as
 * `isDesktopDictationFlagEnabled` rather than as a flag getter (D3a — a
 * contributions file never reads a flag), and `isTauri` is the context field the
 * legacy site read, not the `@/tauri/utils` call some neighbouring entries make.
 *
 * `builds` is omitted, i.e. `cloud-only` (D6), and that preserves behaviour
 * rather than narrowing it: the app composes the registry as the `cloud` variant
 * (`src/composition/registry-atom.ts`), no on-prem queue host exists to
 * aggregate the other arm, and the card is gated on a PostHog flag the on-prem
 * appliance has no provider for — so it cannot show there either way.
 */
export const newInThisReleaseCard = defineQueuedModal({
  id: ModalId.DICTATION_NEW_IN_RELEASE_CARD,
  /**
   * `ModalPriority.LOW`'s NUMBER, verbatim — not the frozen `9580` that R1's
   * priority table assigns this entry.
   *
   * The queue sorts ONE merged list (`modalQueueAtom`) in which every unflipped
   * legacy entry still carries a `ModalPriority` value of 1–30, so a frozen
   * `× 10_000` value here would not re-rank the card, it would hoist it above
   * every modal in the app. The frozen scale becomes correct in the PR that
   * removes the last legacy entry alongside it (task Z3); until then a flipped
   * entry keeps the number it had.
   *
   * The consequence to know before copying this: two flips at the same legacy
   * level collide on `duplicate-priority`, which aggregation reports and CI
   * fails on. That is the prompt to order the pair deliberately — and the trap
   * while doing it is a fractional value, because a user-triggered modal
   * enqueued at the same legacy level currently loses its tie to a launch modal
   * by push order alone.
   */
  priority: 1,
  dismissible: true,
  showOnce: true,
  /**
   * Existing users across both the regular and dictate bundles, on the session
   * page, on desktop, inside the dictation rollout — everyone except a BRAND-NEW
   * dictate signup, to whom dictation is not "new".
   *
   * The prefix covers both the old `/scribe/session/[...id]` and the new
   * `/scribe/sessions/[[...id]]` route patterns, and deliberately excludes bare
   * `/scribe`, a transient redirect/loading index the card would pop over a
   * blank screen.
   *
   * The legacy declaration also carried the config atom's shared `isValidPath`
   * guard. It is not dropped, it is SUBSUMED: no route that guard suppresses
   * begins with `/scribe/session`. `src/atoms/modal-queue-first-flip.test.ts`
   * pins that by reading the guard's route list out of the config atom's source
   * and asserting each one leaves this predicate false, so a route added to the
   * guard later cannot silently escape it.
   */
  eligible: (context) =>
    context.isTauri &&
    context.isDesktopDictationFlagEnabled &&
    context.hasToken &&
    (context.hasOrganisationInfo || context.isNonAdminTeamMember) &&
    context.hasRegion &&
    !(context.isNewUser && context.userSignupSource === SignupSource.dictate) &&
    context.pathname.startsWith('/scribe/session'),
  load: rendersFromItsAppMount,
});
